/**
 * A porta entre a tela da biblioteca técnica e a API do módulo 12.
 *
 * É o único arquivo do módulo que conhece `fetch`, caminho de rota e formato de
 * JSON. Quem está acima dele — `consultas.ts` e a tela — fala em domínio.
 *
 * ## A queda de erro é por SITUAÇÃO, e não uma frase só
 *
 * Mesma decisão do módulo 08, pelo mesmo motivo medido lá: com o servidor
 * rodando uma versão anterior, o `GET` responde 404 e uma frase única diria
 * "confira os campos" numa lista que não tem campo nenhum. Mandar procurar o
 * erro onde ele não está é pior do que não dizer nada.
 */
import { buscar } from '@/lib/licenca-das-rotas';
import { BASE_DA_API, CREDENCIAIS_DA_API } from '@/lib/api';

const BASE = BASE_DA_API;

/** Não há cookie de sessão válido: ninguém entrou, ou o prazo expirou. */
export class SemSessao extends Error {
  constructor() {
    super('não há sessão aberta');
    this.name = 'SemSessao';
  }
}

/** O backend não respondeu. Rede, porta fechada, processo fora do ar. */
export class ApiIndisponivel extends Error {
  constructor() {
    super('o sistema não respondeu');
    this.name = 'ApiIndisponivel';
  }
}

/** O pedido chegou e foi recusado — com o que fazer em seguida. */
export class RecusaDaApi extends Error {
  readonly comoResolver: string;
  readonly status: number;

  constructor(erro: string, comoResolver: string, status: number) {
    super(erro);
    this.name = 'RecusaDaApi';
    this.comoResolver = comoResolver;
    this.status = status;
  }
}

interface CorpoDeErro {
  readonly erro?: string;
  readonly comoResolver?: string;
}

function quedaDoErro(status: number): { erro: string; comoResolver: string } {
  if (status === 403) {
    return {
      erro: 'você não tem acesso a isto',
      comoResolver: 'Peça a quem administra o sistema para liberar esta ação para o seu perfil.',
    };
  }
  if (status === 404) {
    return {
      erro: 'o servidor não conhece este endereço',
      comoResolver:
        'Recarregue a página. Se o sistema acabou de ser atualizado, o servidor ainda está com a versão anterior e precisa ser reiniciado.',
    };
  }
  if (status >= 500) {
    return {
      erro: 'o servidor falhou ao responder',
      comoResolver: 'Tente de novo; se continuar, avise o suporte com o horário em que aconteceu.',
    };
  }
  return {
    erro: 'o pedido não foi aceito',
    comoResolver: 'Confira o que foi preenchido e tente outra vez; se continuar, avise o suporte com o horário.',
  };
}

async function pedir<T>(caminho: string, init?: RequestInit): Promise<T> {
  let resposta: Response;
  try {
    /* A leitura sem a licenca da rota nem sai: ver `@/lib/licenca-das-rotas`. */
    resposta = await buscar(`${BASE}${caminho}`, caminho, {
      credentials: CREDENCIAIS_DA_API,
      headers: init?.body ? { 'content-type': 'application/json' } : {},
      ...init,
    });
  } catch {
    throw new ApiIndisponivel();
  }

  if (resposta.status === 401) throw new SemSessao();

  if (!resposta.ok) {
    const corpo = (await resposta.json().catch(() => ({}))) as CorpoDeErro;
    throw new RecusaDaApi(
      corpo.erro ?? quedaDoErro(resposta.status).erro,
      corpo.comoResolver ?? quedaDoErro(resposta.status).comoResolver,
      resposta.status,
    );
  }

  return (await resposta.json()) as T;
}

const enviar = <T,>(caminho: string, corpo: unknown): Promise<T> =>
  pedir<T>(caminho, { method: 'POST', body: JSON.stringify(corpo) });

const alterar = <T,>(caminho: string, corpo: unknown): Promise<T> =>
  pedir<T>(caminho, { method: 'PATCH', body: JSON.stringify(corpo) });

/* ==========================================================================
 * As linhas, exatamente como a API as devolve
 * ======================================================================= */

/** A que um documento alcança: um alvo, e o que ele estreita. */
export interface AplicacaoDaApi {
  readonly id: string;
  readonly alvo: 'fabricante' | 'modelo' | 'configuracao' | 'equipamento';
  readonly nome: string | null;
  readonly serieDe: string | null;
  readonly serieAte: string | null;
  readonly motorModelo: string | null;
  readonly componente: string | null;
}

export interface DocumentoDaApi {
  readonly id: string;
  readonly titulo: string;
  readonly tipo: string;
  readonly origem: 'empresa' | 'fabricante' | 'procedimento' | 'externo';
  readonly situacao: 'pendente' | 'validado' | 'recusado';
  /**
   * A posição na hierarquia de fontes da seção 5.3 do guia. Menor vence.
   *
   * Ela é calculada no banco a partir da origem E da situação juntas: um
   * documento da empresa só alcança o nível 1 depois de validado. Por isso a
   * tela lê este número em vez de recalcular — duas contas da mesma coisa
   * divergem na primeira correção.
   */
  readonly prioridadeFonte: number;
  readonly idioma: string;
  readonly revisao: string | null;
  readonly publicadoEm: string | null;
  readonly paginas: number | null;
  readonly temArquivo: boolean;
  /** Medidos pelo SERVIDOR a partir do arquivo enviado; nulos antes do envio. */
  readonly mime: string | null;
  readonly tamanhoBytes: number | null;
  readonly temTexto: boolean;
  readonly precisaOcr: boolean;
  /** O texto veio da leitura pela IA (0034), e não da camada do PDF. */
  readonly textoPelaIa: boolean;
  /** Andamento da leitura pela IA: nula quando nunca foi pedida. */
  readonly leituraIa: 'lendo' | 'lida' | 'falhou' | null;
  readonly leituraIaPagina: number | null;
  readonly leituraIaErro: string | null;
  /** A última marca da leitura: sem marca nova por 20 minutos, ela é dada como parada. */
  readonly leituraIaEm: string | null;
  readonly restrito: boolean;
  readonly restricaoNota: string | null;
  readonly observacao: string | null;
  readonly enviadoPor: string | null;
  readonly criadoEm: string;
  readonly aplicacoes: readonly AplicacaoDaApi[];
}

export interface EnvioDeDocumento {
  readonly titulo: string;
  readonly tipo?: string;
  readonly origem: string;
  readonly revisao?: string;
  readonly publicadoEm?: string;
  readonly paginas?: number;
  readonly restrito?: boolean;
  readonly restricaoNota?: string;
  readonly observacao?: string;
  readonly motivo?: string;
}

export interface EnvioDeAplicacao {
  readonly fabricanteId?: string;
  readonly modeloId?: string;
  readonly configuracaoId?: string;
  readonly equipamentoId?: string;
  readonly serieDe?: string;
  readonly serieAte?: string;
  readonly motorModelo?: string;
  readonly componente?: string;
}

/** O que a aplicabilidade pode apontar. Porta própria do módulo 12. */
export interface AlvosDaApi {
  readonly fabricantes: readonly { id: string; nome: string }[];
  readonly modelos: readonly { id: string; nome: string; fabricante: string }[];
  readonly configuracoes: readonly { id: string; nome: string; modelo: string }[];
  readonly equipamentos: readonly { id: string; codigo: string; descricao: string; numeroSerie: string | null }[];
}

export const api = {
  documentos: () => pedir<{ documentos: readonly DocumentoDaApi[] }>('/tecnico/documentos'),

  alvos: () => pedir<AlvosDaApi>('/tecnico/apoio/alvos'),

  criar: (envio: EnvioDeDocumento) => enviar<DocumentoDaApi>('/tecnico/documentos', envio),

  editar: (id: string, envio: Partial<EnvioDeDocumento>) =>
    alterar<DocumentoDaApi>(`/tecnico/documentos/${id}`, envio),

  /**
   * Validar e revogar têm porta PRÓPRIA, e não são um campo do `PATCH`.
   *
   * Validar muda a ORDEM das respostas técnicas: só o documento validado da
   * empresa vence o do fabricante. Revogar tira o documento das respostas no
   * mesmo instante. Estados que mudam o que o sistema responde merecem um ato
   * deliberado, e não um campo editado de passagem.
   */
  validar: (id: string, motivo?: string) =>
    enviar<{ id: string; situacao: string; prioridadeFonte: number }>(
      `/tecnico/documentos/${id}/validacao`,
      motivo === undefined ? {} : { motivo },
    ),

  revogar: (id: string, motivo: string) =>
    enviar<{ id: string; situacao: string; saiuDasRespostas: boolean }>(
      `/tecnico/documentos/${id}/revogacao`,
      { motivo },
    ),

  aplicar: (id: string, envio: EnvioDeAplicacao) =>
    enviar<{ id: string }>(`/tecnico/documentos/${id}/aplicacoes`, envio),

  desaplicar: (documentoId: string, aplicacaoId: string) =>
    pedir<{ removida: boolean }>(`/tecnico/documentos/${documentoId}/aplicacoes/${aplicacaoId}`, {
      method: 'DELETE',
    }),

  /**
   * O ARQUIVO vai cru, e não em JSON nem em formulário: o servidor lê o tipo do
   * CONTEÚDO, mede o tamanho e calcula o hash. O `content-type` mandado aqui é
   * só o que o navegador sabe — o servidor não confia nele.
   */
  enviarArquivo: (id: string, arquivo: File) =>
    pedir<DocumentoDaApi>(`/tecnico/documentos/${id}/arquivo`, {
      method: 'PUT',
      body: arquivo,
      headers: { 'content-type': arquivo.type || 'application/octet-stream' },
    }),

  /** Pede a leitura do manual escaneado pela IA (0034). Ela roda no servidor, em segundo plano. */
  lerPelaIa: (id: string) => enviar<{ lendo: true; paginas: number }>(`/tecnico/documentos/${id}/leitura-pela-ia`, {}),

  /** O índice por sentido: quantos trechos já têm vetor. */
  indice: () =>
    pedir<{ ligado: boolean; trechos: number; comVetor: number; pendentes: number; trechosRestritos: number }>('/tecnico/indice'),

  completarIndice: () => enviar<{ indexando: true }>('/tecnico/indice', {}),

  /** Uma URL que vale alguns minutos, e não o arquivo: o manual vai do armazenamento direto ao navegador. */
  urlDoArquivo: (id: string) =>
    pedir<{ url: string; expiraEmSegundos: number; mime: string | null }>(`/tecnico/documentos/${id}/arquivo`),
} as const;
